import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
  FilenameConventionDeleteResult,
  FilenameConventionService,
} from 'src/app/services/filename-conventions/filename-convention.service';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';

export interface FilenameConventionDeleteDialogData {
  organizationId: string;
  current: FilenameConvention;
}

interface DependentsWarning {
  count: number;
  names: string[];
}

@Component({
  selector: 'app-filename-convention-delete-dialog',
  templateUrl: './filename-convention-delete-dialog.component.html',
  styleUrls: ['./filename-convention-delete-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class FilenameConventionDeleteDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly acknowledgeControl = new FormControl<boolean>(false, { nonNullable: true });

  warning: DependentsWarning | null = null;
  probed = false;
  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FilenameConventionDeleteDialogData,
    private readonly dialogRef: MatDialogRef<FilenameConventionDeleteDialogComponent, boolean>,
    private readonly service: FilenameConventionService,
  ) {
    this.probeDependents();
  }

  get needsAcknowledge(): boolean {
    return this.warning !== null && this.warning.count > 0;
  }

  get canDelete(): boolean {
    if (this.submitting || !this.probed) return false;
    if (this.needsAcknowledge && !this.acknowledgeControl.value) return false;
    return true;
  }

  confirm(): void {
    if (!this.canDelete) return;
    this.submitting = true;
    this.service
      .remove(this.data.organizationId, this.data.current.id, true, this.modeHint)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private probeDependents(): void {
    this.service
      .remove(this.data.organizationId, this.data.current.id, false, this.modeHint)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: FilenameConventionDeleteResult) => {
          this.warning = result.warning;
          this.probed = true;
        },
        error: () => {
          this.probed = true;
        },
      });
  }

  private get modeHint(): 'rangeBased' | 'evBased' {
    return this.data.current.value.payload.mode;
  }
}
